import React, { type ElementType, type ComponentPropsWithoutRef } from 'react';
import { cn } from '@/app/_lib/utils';

/**
 * Copytext - Core Component
 *
 * Tokenized typography styles from design-tokens-oklch.css:
 * - default/book: body-md (16px/24px)
 * - lead: body-xl (20px/28px)
 * - large: body-lg (18px/28px)
 * - small/muted: body-sm fallback (14px/20px) - no token yet
 *
 * @see copytext.token.css for CSS custom properties
 * @see https://www.figma.com/design/4fWGiNz1BdD4uMFX5Wfarg/SPARKS-Core-Kit-Foundations?node-id=22-1400
 */

export type CopytextVariant = 'default' | 'lead' | 'large' | 'large-muted' | 'small' | 'muted' | 'book' | 'book-muted';

const variantStyles: Record<CopytextVariant, string> = {
  default: 'font-body-md-typeface lg:font-body-md-typeface-lg text-body-md lg:text-body-md-lg font-body-md lg:font-body-md-lg tracking-body-md lg:tracking-body-md-lg text-inherit mb-6',
  lead: 'font-body-xl-typeface lg:font-body-xl-typeface-lg text-body-xl lg:text-body-xl-lg font-body-xl lg:font-body-xl-lg tracking-body-xl lg:tracking-body-xl-lg text-muted-foreground mb-6',
  large: 'font-body-lg-typeface lg:font-body-lg-typeface-lg text-body-lg lg:text-body-lg-lg font-body-lg lg:font-body-lg-lg tracking-body-lg lg:tracking-body-lg-lg text-inherit mb-6',
  'large-muted': 'font-body-lg-typeface lg:font-body-lg-typeface-lg text-body-lg lg:text-body-lg-lg font-body-lg lg:font-body-lg-lg tracking-body-lg lg:tracking-body-lg-lg text-muted-foreground mb-6',
  small: 'font-body-sm-typeface lg:font-body-sm-typeface-lg text-body-sm lg:text-body-sm-lg font-body-sm lg:font-body-sm-lg tracking-body-sm lg:tracking-body-sm-lg text-inherit mb-6',
  muted: 'font-body-sm-typeface lg:font-body-sm-typeface-lg text-body-sm lg:text-body-sm-lg font-body-sm lg:font-body-sm-lg tracking-body-sm lg:tracking-body-sm-lg text-muted-foreground mb-6',
  book: 'font-body-md-typeface lg:font-body-md-typeface-lg text-body-md lg:text-body-md-lg tracking-body-md lg:tracking-body-md-lg font-normal text-inherit mb-6',
  'book-muted': 'font-body-md-typeface lg:font-body-md-typeface-lg text-body-md lg:text-body-md-lg tracking-body-md lg:tracking-body-md-lg font-normal text-muted-foreground mb-6',
};

/**
 * Polymorphic Copytext props - inherits HTML attributes based on the `as` prop
 */
export type CopytextProps<T extends ElementType = 'p'> = {
  children: React.ReactNode;
  className?: string;
  /**
   * The visual style variant of the copytext
   * @default 'default'
   */
  variant?: CopytextVariant;
  /**
   * The HTML element to render
   * @default 'p'
   */
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, 'children' | 'className' | 'variant' | 'as'>;

export function Copytext<T extends ElementType = 'p'>({
  children,
  className,
  variant = 'default',
  as,
  ...props
}: CopytextProps<T>) {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- `as` is a polymorphic ElementType (includes bare string), so an empty string must also fall back to 'p'; `||` is intended, not a nullish check
  const Tag = as || 'p';
  return (
    <Tag className={cn(variantStyles[variant], className)} {...props}>
      {children}
    </Tag>
  );
}
